import { useQuery } from '@apollo/client';
import Clearbit from '@clearkit/icons/glyphs/Clearbit';
import DirectionForward from '@clearkit/icons/glyphs/DirectionForward';
import GoExternal from '@clearkit/icons/glyphs/GoExternal';
import Salesforce from '@clearkit/icons/glyphs/Salesforce';
import {
  CKAvatar,
  CKBox,
  CKButton,
  CKCompanyLogo,
  CKDrawer,
  CKField,
  CKFieldLeftElement,
  CKTag,
} from '@clearkit/react';
import capitalize from 'lodash/capitalize';
import moment from 'moment';

import GraphqlError from '~/components/GraphqlError';
import LoadingArea from '~/components/LoadingArea';
import MissingPage from '~/components/MissingPage';
import { readableType, statuses, subTypes } from '~/lib/dataEnrichment';

import query from './query.graphql';

export default function EnrichmentLogsShow({ id }) {
  const {
    data: { enrichmentLog, accountConnector },
    error,
    loading,
    refetch,
  } = useQuery(query, {
    fetchPolicy: 'cache-first',
    returnPartialData: true,
    variables: {
      id: id,
    },
  });

  if (loading) {
    return (
      <div className="x-sidebar">
        <LoadingArea loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="x-sidebar">
        <GraphqlError error={error} refetch={refetch} />;
      </div>
    );
  }

  if (!enrichmentLog) {
    return (
      <div className="x-sidebar">
        <MissingPage />
      </div>
    );
  }

  const enrichmentLogReadableType = readableType(enrichmentLog.type);
  const showTypeInfo = enrichmentLog.externalId != 'unknown';
  const sfdcRecordOverviewUrl = `${accountConnector.remoteMeta?.salesforceInstanceUrl}/${enrichmentLog.externalId}`;

  return (
    <div className="x-sidebar">
      <CKDrawer.Header>
        <div className="flex items-center flex-1 mb-4">
          <EnrichmentLogImage
            className="mr-4 flex-0"
            enrichmentLog={enrichmentLog}
          />

          <div className="flex flex-col flex-1">
            <CKDrawer.Heading>
              {enrichmentLog.name || enrichmentLog.identifier}
            </CKDrawer.Heading>

            {enrichmentLog.name != null ? (
              <span className="text-base text-blue-500">
                {enrichmentLog.identifier}
              </span>
            ) : null}
          </div>

          <CKButton
            isDisabled={enrichmentLog.externalId === 'unknown'}
            as="a"
            target="_blank"
            href={sfdcRecordOverviewUrl}
            className="flex-0"
            rightIcon={<GoExternal />}
            variant={CKButton.Variant.simple}
            variantColor={CKButton.VariantColor.blue}>
            Go To {enrichmentLogReadableType} Record
          </CKButton>
        </div>

        <div className="flex items-center justify-between flex-1">
          <span className="text-sm text-gray-400">
            Clearbit Trace ID: {enrichmentLog.id}
          </span>

          {showTypeInfo ? (
            <CKTag>
              {enrichmentLogReadableType} ID: {enrichmentLog.externalId}
            </CKTag>
          ) : null}
        </div>
      </CKDrawer.Header>

      <CKDrawer.Body className="px-10 py-6">
        <ol className="space-y-3.5">
          {enrichmentLog.steps.map((enrichmentLogStep, index) => (
            <li key={index}>
              <EnrichmentLogStep enrichmentLogStep={enrichmentLogStep} />
            </li>
          ))}
        </ol>
      </CKDrawer.Body>
    </div>
  );
}
function EnrichmentLogImage({ className, enrichmentLog }) {
  switch (enrichmentLog.subType) {
    case subTypes.account:
      return (
        <CKCompanyLogo
          className={className}
          cornerRadius={50}
          domain={enrichmentLog.identifier}
          size={50}
        />
      );
    case subTypes.contact:
    case subTypes.lead:
      return (
        <CKAvatar
          className={className}
          name={enrichmentLog.name || enrichmentLog.identifier}
          size={50}
          src={enrichmentLog.image}
        />
      );
    default:
      return null;
  }
}

const TIMESTAMP_FORMAT = 'MMM Do YYYY, h:mm a';

function EnrichmentLogStep({ enrichmentLogStep }) {
  const [isOpen, setIsOpen] = useState(true);

  const iconClassName = isOpen
    ? 'select-none cursor-pointer text-blue-500 transform rotate-90'
    : 'select-none cursor-pointer text-gray-400';

  const tagClassName = classnames(
    'ml-6 inline-flex whitespace-nowrap items-center font-normal text-xs text-white leading-5 rounded-sm px-2',
    {
      'bg-blue-300': enrichmentLogStep.status === statuses.complete,
      'bg-gray-400': enrichmentLogStep.status === statuses.pending,
      'bg-gray-500': enrichmentLogStep.status === statuses.skipped,
      'bg-red-500': enrichmentLogStep.status === statuses.error,
      'bg-yellow-500': enrichmentLogStep.status === statuses.info,
    },
  );

  const formattedTimestamp = moment(enrichmentLogStep.timestamp).format(
    TIMESTAMP_FORMAT,
  );

  function attrIcon(enrichmentLogStepName) {
    if (enrichmentLogStepName.match(/salesforce/i)) {
      return <Salesforce width={20} className="text-blue-400" />;
    } else {
      return <Clearbit width={20} className="text-blue-400" />;
    }
  }

  function stepToColumnSpanClass(enrichmentLogStepName) {
    if (enrichmentLogStepName.match(/salesforce/i)) {
      return 'col-span-1';
    } else {
      return 'col-span-2';
    }
  }

  return (
    <CKBox variant={CKBox.Variant['tinted-frame']}>
      <div className="flex items-center p-4">
        {enrichmentLogStep.changes.length > 0 ? (
          <DirectionForward
            className={iconClassName}
            onClick={() => {
              const newIsOpen = !isOpen;

              setIsOpen(newIsOpen);
            }}
          />
        ) : null}

        <span className={tagClassName}>{enrichmentLogStep.name}</span>

        <span className="ml-6 text-gray-700 text-md">
          {capitalize(enrichmentLogStep.status)}
        </span>

        <time
          dateTime={enrichmentLogStep.timestamp}
          className="ml-auto text-sm font-normal text-gray-500">
          {formattedTimestamp}
        </time>
      </div>

      {enrichmentLogStep.message != capitalize(enrichmentLogStep.status) ? (
        <div className="flex p-4 pt-0 text-gray-500 text-md">
          {enrichmentLogStep.message}
        </div>
      ) : null}

      {isOpen && Boolean(enrichmentLogStep.changes.length) ? (
        <div className="py-4 mx-4 border-t border-gray-200">
          <div className="mb-1 grid grid-cols-3 gap-x-6">
            <span className="text-sm font-semibold text-gray-500">
              Attribute
            </span>

            {enrichmentLogStep.name.match(/salesforce/i) ? (
              <span className="text-sm font-semibold text-gray-500">
                Original Value
              </span>
            ) : null}

            <span className="text-sm font-semibold text-gray-500">
              New Value
            </span>
          </div>

          <div className="grid grid-cols-3 gap-x-6 gap-y-3">
            {enrichmentLogStep.changes.map((enrichmentLogStepChange) => (
              <>
                <CKField className="font-medium text-gray-600 bg-gray-100 rounded px-2.5">
                  <div className="pl-8 text-base font-medium text-gray-600 truncate">
                    {enrichmentLogStepChange.fieldName}
                  </div>
                  <CKFieldLeftElement>
                    {attrIcon(enrichmentLogStep.name)}
                  </CKFieldLeftElement>
                </CKField>

                {enrichmentLogStep.name.match(/salesforce/i) ? (
                  <div className="py-px text-base font-medium text-gray-600 truncate bg-gray-100 rounded px-2.5 leading-8">
                    {enrichmentLogStepChange.from}
                  </div>
                ) : null}

                <div
                  className={classnames(
                    'py-px text-base font-medium text-gray-600 truncate bg-gray-100 rounded px-2.5 leading-8',
                    stepToColumnSpanClass(enrichmentLogStep.name),
                  )}>
                  {enrichmentLogStepChange.to}
                </div>
              </>
            ))}
          </div>
        </div>
      ) : null}
    </CKBox>
  );
}
